import { createReducer } from '@reduxjs/toolkit'
import { cloneDeep } from 'lodash'

import { TGroupApi } from 'types/GlobalGroup'

import { arrayToObject } from 'utils/functions'

import * as groupActions from '../../actions/Groups/actions'
import * as keyMetricActions from '../../actions/KeyMetrics/actions'
import * as accountActions from '../../actions/Accounts/actions'
import * as authenticationActions from '../../actions/Authentication/actions'
import { EReduxState } from 'redux/utils/State'

export type GroupsBody = { [key: string]: TGroupApi }

export interface GroupReducerType {
  data: GroupsBody
  state: EReduxState
  isAddingMetric: boolean
}

const INITIAL_STATE: GroupReducerType = {
  data: {},
  state: EReduxState.IDLE,
  isAddingMetric: false
}

// Reducer
const groupReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    // tryGetAllGroups
    .addCase(groupActions.getAll, (state) => {
      state.state = EReduxState.LOADING
    })
    .addCase(groupActions.getAllSuccess, (state, action) => {
      state.data = arrayToObject(action.payload.data)
      state.state = EReduxState.SUCCESS
    })
    .addCase(groupActions.getAllFailed, (state) => {
      state.state = EReduxState.ERROR
    })

    // tryCreateGroup
    .addCase(groupActions.createSuccess, (state, action) => {
      state.data[action.payload.data.id] = action.payload.data
    })

    // tryUpdateGroup
    .addCase(groupActions.updateSuccess, (state, action) => {
      state.data[action.payload.data.id] = action.payload.data
    })

    // tryDeleteGroup
    .addCase(groupActions.destroySuccess, (state, action) => {
      const newData = cloneDeep(state.data)

      delete newData[action.payload]

      state.data = newData
    })

    // tryAddMetricToGroup
    .addCase(groupActions.addMetric, (state) => {
      state.isAddingMetric = true
    })
    .addCase(groupActions.addMetricSuccess, (state, action) => {
      state.isAddingMetric = false
      state.data[action.payload.data.id] = action.payload.data
    })
    .addCase(groupActions.addMetricFailed, (state) => {
      state.isAddingMetric = false
    })

    // tryAddKeyMetricToGroup
    .addCase(groupActions.addKeyMetricSuccess, (state, action) => {
      state.data[action.payload.data.id] = action.payload.data
    })

    // tryRemoveMetricFromGroup
    .addCase(groupActions.removeMetricSuccess, (state, action) => {
      state.data[action.payload.data.id] = action.payload.data
    })

    // tryRemoveKeyMetricFromGroup
    .addCase(groupActions.removeKeyMetricSuccess, (state, action) => {
      state.data[action.payload.data.id] = action.payload.data
    })

    // tryUpdateGroupMember
    .addCase(groupActions.updateMemberSuccess, (state, action) => {
      const { groupId, member } = action.payload

      state.data[groupId].members = state.data[groupId].members.map((m) =>
        m.id === member.id ? member : m
      )
    })

    // tryDeleteGroupMember
    .addCase(groupActions.deleteMemberSuccess, (state, action) => {
      const { groupId, id } = action.payload

      state.data[groupId].members = state.data[groupId].members.filter(
        (m) => m.id !== id
      )
    })

    // tryUpdateKeyMetric (group_ids changed)
    .addCase(keyMetricActions.updateSuccess, (state, action) => {
      const { group_ids, id } = action.payload
      const newGroupIds = new Set(group_ids ?? [])

      Object.keys(state.data).forEach((groupId) => {
        const group = state.data[groupId]
        const hadKm = group.key_metrics.includes(id)
        const shouldHaveKm = newGroupIds.has(groupId)

        if (hadKm && !shouldHaveKm) {
          group.key_metrics = group.key_metrics.filter((kmId) => kmId !== id)
        } else if (!hadKm && shouldHaveKm) {
          group.key_metrics = [...group.key_metrics, id]
        }
      })
    })

    // Group pin actions
    .addCase(groupActions.pinKeyMetricOnGroupSuccess, (state, action) => {
      const { groupId, pin } = action.payload

      state.data[groupId].pins = [...state.data[groupId].pins, pin]
    })
    .addCase(groupActions.unpinKeyMetricOnGroupSuccess, (state, action) => {
      const { groupId, pinId } = action.payload

      state.data[groupId].pins = state.data[groupId].pins.filter(
        (p) => p.id !== pinId
      )
    })
    .addCase(groupActions.updateGroupPinCommentSuccess, (state, action) => {
      const { comment, groupId, pinId } = action.payload

      const pin = state.data[groupId].pins.find((p) => p.id === pinId)

      if (pin) pin.comment = comment
    })

    // Dashboard actions
    .addCase(groupActions.appendToDashboard, (state, action) => {
      const { groupId, dashboardGroupId } = action.payload

      state.data[groupId].dashboards = [
        ...state.data[groupId].dashboards,
        dashboardGroupId
      ]
    })
    .addCase(groupActions.removeFromDashboard, (state, action) => {
      const { groupId, dashboardGroupId } = action.payload

      if (groupId) {
        state.data[groupId].dashboards = state.data[groupId].dashboards.filter(
          (id) => id !== dashboardGroupId
        )
      } else {
        // remove dashboard from all groups
        Object.keys(state.data).forEach((key) => {
          state.data[key].dashboards = state.data[key].dashboards.filter(
            (id) => id !== dashboardGroupId
          )
        })
      }
    })

    // Account actions that affect groups
    .addCase(accountActions.updateSuccess, (state, action) => {
      if (action.payload.groups) {
        state.data = arrayToObject(action.payload.groups)
      }
    })
    .addCase(accountActions.createSuccess, (state, action) => {
      if (action.payload.groups) {
        state.data = arrayToObject(action.payload.groups)
      }
    })
    .addCase(accountActions.destroySuccess, (state, action) => {
      if (action.payload.groups) {
        state.data = arrayToObject(action.payload.groups)
      }
    })

    // Sign out
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
    .addDefaultCase((state) => state)
})

export default groupReducer
